import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface AuthDebugProps {
  isVisible?: boolean;
}

const AuthDebug: React.FC<AuthDebugProps> = ({ isVisible = false }) => {
  const [authInfo, setAuthInfo] = useState<any>(null);
  const [rolesInfo, setRolesInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { user, hasAdminAccess, isAdmin, isSuperAdmin, getUserRoles, refreshUser } = useAuth();

  useEffect(() => {
    if (isVisible) {
      // Note: Now using OIDC authentication instead of Azure AD
      setAuthInfo({ 
        message: 'Using OIDC authentication via UniFirst SSO',
        provider: 'OIDC',
        note: 'Auth info is now handled through OIDC service, not Azure AD endpoints'
      });
      
      // Test OIDC configuration
      fetch('/api/config/oidc')
        .then(response => response.json())
        .then(data => {
          setRolesInfo(data);
          console.log('OIDC config response:', data);
        })
        .catch(roleError => {
          console.error('OIDC config error:', roleError);
          setError('OIDC configuration failed: ' + roleError.message);
        });
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: 'white',
      border: '1px solid #ccc',
      padding: '15px',
      borderRadius: '8px',
      fontSize: '12px',
      maxWidth: '400px',
      maxHeight: '80vh',
      overflow: 'auto',
      zIndex: 9999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
    }}>
      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>🔍 Auth Debug Panel</h4>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Current User Email:</strong> {user?.userDetails || 'Not available'}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Frontend Roles:</strong> {JSON.stringify(getUserRoles())}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Has Admin Access:</strong> <span style={{ color: hasAdminAccess() ? 'green' : 'red', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {hasAdminAccess() ? <><CheckCircle style={{ height: '16px', width: '16px' }} /> YES</> : <><XCircle style={{ height: '16px', width: '16px' }} /> NO</>}
        </span>
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Is Admin:</strong> <span style={{ color: isAdmin() ? 'green' : 'red', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {isAdmin() ? <><CheckCircle style={{ height: '16px', width: '16px' }} /> YES</> : <><XCircle style={{ height: '16px', width: '16px' }} /> NO</>}
        </span>
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Is Super Admin:</strong> <span style={{ color: isSuperAdmin() ? 'green' : 'red', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {isSuperAdmin() ? <><CheckCircle style={{ height: '16px', width: '16px' }} /> YES</> : <><XCircle style={{ height: '16px', width: '16px' }} /> NO</>}
        </span>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>OIDC Authentication Status:</strong>
        {authInfo ? (
          <pre style={{ fontSize: '10px', margin: '5px 0', padding: '5px', background: '#f5f5f5', overflow: 'auto' }}>
            {JSON.stringify(authInfo, null, 2)}
          </pre>
        ) : (
          <span> Loading...</span>
        )}
      </div>

      <div style={{ marginBottom: '10px' }}>
        <strong>OIDC Configuration:</strong>
        {rolesInfo ? (
          <pre style={{ fontSize: '10px', margin: '5px 0', padding: '5px', background: '#e8f5e8', overflow: 'auto' }}>
            {JSON.stringify(rolesInfo, null, 2)}
          </pre>
        ) : (
          <span> Loading...</span>
        )}
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '10px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        <button 
          onClick={() => refreshUser()}
          style={{ fontSize: '10px', padding: '5px 8px' }}
        >
          🔄 Refresh Auth
        </button>
        <button 
          onClick={() => window.location.href = '/admin'}
          style={{ fontSize: '10px', padding: '5px 8px' }}
        >
          🚀 Try Admin Page
        </button>
        <button 
          onClick={() => {
          }}
          style={{ fontSize: '10px', padding: '5px 8px' }}
        >
          📝 Log to Console
        </button>
      </div>
    </div>
  );
};

export default AuthDebug; 